import { mapGetters, mapMutations } from 'vuex'
import cognitoService from '@/utils/cognito'
import axios from 'axios'
import { GOOGLE_PASSWORD_RESET_MESSAGE, shouldBlockPasswordReset } from './login.helpers'
import { appendShopifySignupProof, shopifyProofTokenFields } from '@/utils/shopify-signup-proof'

export default {
  name: 'Login',
  data () {
    return {
      valid: false,
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      error: null,
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters'
      ],
      // Forgot password
      showForgotPassword: false,
      showResetCode: false,
      resetEmail: '',
      resetCode: '',
      newPassword: '',
      showNewPassword: false,
      resetLoading: false
    }
  },
  computed: {
    ...mapGetters([
      'apiUrl'
    ])
  },
  methods: {
    ...mapMutations([
      'setAuthToken'
    ]),

    signInWithGoogle () {
      const next = encodeURIComponent(window.location.origin + '/auth/in?token=')
      const url = `${this.apiUrl}/auth/cognito?next=${next}&identity_provider=Google`
      window.location.href = appendShopifySignupProof(url, shopifyProofTokenFields(this.$route.query))
    },

    async signInWithEmail () {
      if (!this.$refs.loginForm.validate()) return

      this.loading = true
      this.error = null

      try {
        // Authenticate with Cognito
        const tokens = await cognitoService.signIn(this.email, this.password)

        // Exchange Cognito tokens for our JWT session token
        const response = await axios.post(`${this.apiUrl}/auth/cognito/token`, {
          accessToken: tokens.accessToken,
          idToken: tokens.idToken,
          refreshToken: tokens.refreshToken,
          ...shopifyProofTokenFields(this.$route.query)
        })

        if (response.data.token) {
          this.setAuthToken(response.data.token)
          this.$router.push('/workspace')
        } else {
          throw new Error('No token received')
        }
      } catch (err) {
        console.error('Sign in error:', err)
        if (err.code === 'UserNotConfirmedException') {
          this.error = 'Please verify your email first. Check your inbox for a verification code.'
        } else if (err.code === 'NotAuthorizedException') {
          this.error = 'Incorrect email or password'
        } else if (err.code === 'UserNotFoundException') {
          this.error = 'No account found with this email'
        } else {
          this.error = err.message || 'Sign in failed. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async sendResetCode () {
      if (!this.resetEmail) return

      this.resetLoading = true
      this.error = null

      try {
        try {
          const checkResponse = await axios.post(`${this.apiUrl}/auth/check-email`, {
            email: this.resetEmail
          })
          if (shouldBlockPasswordReset(checkResponse.data)) {
            this.showForgotPassword = false
            this.error = GOOGLE_PASSWORD_RESET_MESSAGE
            return
          }
        } catch (checkErr) {
          console.error('Forgot password email check failed:', checkErr)
        }

        await cognitoService.forgotPassword(this.resetEmail)
        this.showForgotPassword = false
        this.showResetCode = true
      } catch (err) {
        console.error('Forgot password error:', err)
        this.error = err.message || 'Failed to send reset code'
      } finally {
        this.resetLoading = false
      }
    },

    async confirmResetPassword () {
      if (!this.resetCode || !this.newPassword) return

      this.resetLoading = true

      try {
        await cognitoService.confirmPassword(this.resetEmail, this.resetCode, this.newPassword)
        this.showResetCode = false
        this.resetCode = ''
        this.newPassword = ''
        this.error = null
        // Show success message
        alert('Password reset successful! You can now sign in with your new password.')
      } catch (err) {
        console.error('Confirm password error:', err)
        this.error = err.message || 'Failed to reset password'
      } finally {
        this.resetLoading = false
      }
    }
  }
}
